import { FormValidator } from "../formValidator";
import { ValidationError, ValidationErrors } from "src/errors";
import { EuRelabelCheckInFormDataKey, EuRelabelCheckInProductRow } from "src/types";
import { isInvalidPurchaseOrderNumber, isInvalidQuantity } from "../stringValidation";
import { isInvalidISODate } from "src/helpers/dateHelper";

export class EuRelabelCheckInFormValidator extends FormValidator {
  private static validatePurchaseOrderNumber(value: unknown): ValidationError<EuRelabelCheckInFormDataKey> | undefined {
    if (value === undefined || value === null || value === "") return undefined;
    return isInvalidPurchaseOrderNumber(value)
      ? new ValidationError("Invalid purchase order number", "purchaseOrderId")
      : undefined;
  }

  private static validateTransferOrderNumber(value: unknown): ValidationError<EuRelabelCheckInFormDataKey> | undefined {
    if (value === undefined || value === null || value === "") return undefined;
    return typeof value === "string" && /^TO-[A-Z0-9]+$/i.test(value)
      ? undefined
      : new ValidationError("Invalid transfer order id", "transferOrderId");
  }

  private static validateProducts(value: unknown): ValidationError<EuRelabelCheckInFormDataKey> | undefined {
    const products = value as EuRelabelCheckInProductRow[];

    if (!products || products.length === 0) {
      return new ValidationError("No products found", "products");
    }

    for (const product of products) {
      if (product.receivedQty !== "" && isInvalidQuantity(product.receivedQty, 0, 600000)) {
        return new ValidationError(`Invalid received quantity for ${product.name}`, "products");
      }

      const hasDate = product.productDate !== "";
      const hasType = product.dateType !== "";
      if (hasDate !== hasType) {
        return new ValidationError(`Pick both a date and its type for ${product.name}`, "products");
      }

      if (hasDate && isInvalidISODate(product.productDate)) {
        return new ValidationError(`Enter a valid date for ${product.name} (example format: '01/01/2026')`, "products");
      }
    }

    const hasAtLeastOneReceivedOrShortage = products.some(
      (p) => (p.receivedQty !== "" && Number(p.receivedQty) > 0) || p.shortage !== undefined
    );
    if (!hasAtLeastOneReceivedOrShortage) {
      return new ValidationError(
        "At least one product must have a received quantity greater than 0 or a logged shortage",
        "products"
      );
    }

    return undefined;
  }

  private static readonly validationStrategies: Record<
    EuRelabelCheckInFormDataKey,
    (value: unknown) => ValidationError<EuRelabelCheckInFormDataKey> | undefined
  > = {
    purchaseOrderId: EuRelabelCheckInFormValidator.validatePurchaseOrderNumber,
    transferOrderId: EuRelabelCheckInFormValidator.validateTransferOrderNumber,
    products: EuRelabelCheckInFormValidator.validateProducts
  };

  static validateEuRelabelCheckInFormData<T extends EuRelabelCheckInFormDataKey>(
    formData: Partial<Record<T, unknown>>
  ): ValidationErrors<T> {
    return super.validateFormData(formData, EuRelabelCheckInFormValidator.validationStrategies as never);
  }
}
